import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {
  // Carousel del pannello di sinistra: 0 = branding Atlas, 1..3 = foto (sona, moto, sona2)
  readonly slides = [0, 1, 2, 3];
  activeSlide = 0;
  private readonly carouselIntervalMs = 6000;
  private carouselTimer: ReturnType<typeof setInterval> | null = null;

  loginForm: FormGroup;
  isSubmitted = false;
  isLoading = false;
  generalError: string = '';
  showGeneralError: boolean = false;
  showPassword: boolean = false;

  constructor(private fb: FormBuilder, private authService: AuthService, private router: Router) {
    console.log('Initializing LoginComponent');
    this.loginForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.minLength(5),
          Validators.maxLength(50)
        ]
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(20)
        ]
      ]
    });
    console.log('Login form initialized:', this.loginForm);
  }

  ngOnInit(): void {
    this.resumeCarousel();
  }

  ngOnDestroy(): void {
    this.pauseCarousel();
  }

  // Click su un puntino: vai alla slide e fai ripartire il timer da zero
  goToSlide(index: number): void {
    this.activeSlide = index;
    this.resumeCarousel();
  }

  // In pausa mentre il mouse è sopra il pannello
  pauseCarousel(): void {
    if (this.carouselTimer !== null) {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    }
  }

  resumeCarousel(): void {
    this.pauseCarousel();
    this.carouselTimer = setInterval(() => {
      this.activeSlide = (this.activeSlide + 1) % this.slides.length;
    }, this.carouselIntervalMs);
  }

  getErrorMessage(field: string): string {
    console.log('Getting error message for field:', field);
    const control = this.loginForm.get(field);
    
    // Errori di validazione form
    if (control?.hasError('required')) {
      console.log(`Field ${field} has 'required' error`);
      return `LOGIN.ERROR.${field.toUpperCase()}.REQUIRED`;
    }
    if (control?.hasError('email')) {
      console.log(`Field ${field} has 'email' error`);
      return `LOGIN.ERROR.${field.toUpperCase()}.INVALID`;
    }
    if (control?.hasError('minlength')) {
      console.log(`Field ${field} has 'minlength' error`);
      return `LOGIN.ERROR.${field.toUpperCase()}.MIN_LENGTH`;
    }
    if (control?.hasError('maxlength')) {
      console.log(`Field ${field} has 'maxlength' error`);
      return `LOGIN.ERROR.${field.toUpperCase()}.MAX_LENGTH`;
    }
    
    // Errori di autenticazione dal server
    if (control?.hasError('notFound')) {
      console.log(`Field ${field} has 'notFound' error`);
      return `LOGIN.ERROR.${field.toUpperCase()}.NOT_FOUND`;
    }
    
    console.log(`No errors found for field: ${field}`);
    return '';
  }
  // Nuovo metodo per ottenere errori generali del form
  getGeneralError(): string {
    const formErrors = this.loginForm.errors;
    
    if (formErrors?.['invalidCredentials']) {
      return 'LOGIN.ERROR.GENERAL.INVALID_CREDENTIALS';
    }
    if (formErrors?.['serverError']) {
      return 'LOGIN.ERROR.GENERAL.SERVER_ERROR';
    }
    if (formErrors?.['networkError']) {
      return 'LOGIN.ERROR.GENERAL.NETWORK_ERROR';
    }
    if (formErrors?.['unauthorized']) {
      return 'LOGIN.ERROR.GENERAL.UNAUTHORIZED';
    }
    if (formErrors?.['badRequest']) {
      return 'LOGIN.ERROR.GENERAL.BAD_REQUEST';
    }
    if (formErrors?.['tokenExpired']) {
      return 'LOGIN.ERROR.GENERAL.TOKEN_EXPIRED';
    }
    if (formErrors?.['unknownError']) {
      return 'LOGIN.ERROR.GENERAL.UNKNOWN_ERROR';
    }
    
    return '';
  }
  // Metodo per verificare se ci sono errori generali
  hasGeneralError(): boolean {
    return !!(this.loginForm.errors && (
      this.loginForm.errors['invalidCredentials'] ||
      this.loginForm.errors['serverError'] || 
      this.loginForm.errors['networkError'] || 
      this.loginForm.errors['unauthorized'] ||
      this.loginForm.errors['badRequest'] ||
      this.loginForm.errors['tokenExpired'] ||
      this.loginForm.errors['unknownError']
    ));
  }

  hasError(field: string): boolean {
    const control = this.loginForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched || this.isSubmitted));
  }
  onSubmit(): void {
    console.log('Form submission started');
    this.isSubmitted = true;
    if (this.loginForm.valid) {
      console.log('Form is valid, proceeding with login');
      this.isLoading = true;
      const { email, password } = this.loginForm.value;
      console.log('Form values:', { email, password });

      this.authService.login(email, password).then((isAuthenticated) => {
        console.log('Authentication response received:', isAuthenticated);
        if (isAuthenticated) {
          console.log('Authentication successful, navigating to dashboard');
          this.router.navigate(['/dashboard']);
        } else {
          this.handleLoginError('Credenziali non valide');
        }
      }).catch((error: Error) => {
        console.error('Error during authentication:', error);
        this.handleLoginError(error.message);
      }).finally(() => {
        this.isLoading = false;
        console.log('Loading animation stopped');
      });

    } else {
      console.log('Form is invalid, submission aborted');
    }  }

  private handleLoginError(errorMessage: string): void {
    this.isSubmitted = false;
    this.showGeneralError = true;
    
    // Reset errori precedenti
    this.loginForm.get('email')?.setErrors(null);
    this.loginForm.get('password')?.setErrors(null);
    this.loginForm.setErrors(null);
    
    // Gestione errori specifici basata sui codici HTTP e messaggi
    if (errorMessage.includes('401') || errorMessage.includes('Credenziali non valide') || errorMessage.includes('Unauthorized')) {
      this.loginForm.setErrors({ invalidCredentials: true });
    } else if (errorMessage.includes('400') || errorMessage.includes('Bad Request') || errorMessage.includes('Input non valido')) {
      this.loginForm.setErrors({ badRequest: true });
    } else if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error') || errorMessage.includes('server')) {
      this.loginForm.setErrors({ serverError: true });
    } else if (errorMessage.includes('network') || errorMessage.includes('Network') || errorMessage.includes('connettersi')) {
      this.loginForm.setErrors({ networkError: true });
    } else if (errorMessage.includes('token') && errorMessage.includes('expired')) {
      this.loginForm.setErrors({ tokenExpired: true });
    } else {
      // Errore generico non categorizzato
      this.loginForm.setErrors({ unknownError: true });
    }
    
    // Nascondi l'errore generale dopo 5 secondi
    setTimeout(() => {
      this.showGeneralError = false;
    }, 5000);
    
    console.error('Login failed:', errorMessage);
  }

  // Metodo per resettare gli errori quando l'utente inizia a digitare
  onInputChange(): void {
    if (this.showGeneralError) {
      this.showGeneralError = false;
      this.loginForm.setErrors(null);
    }
  }
}
